import { memo } from 'react';

import { useTranslation } from 'react-i18next';

import { cn } from '@/lib/utils';
import { isKeyEnter } from '@/util/util';

export type AuthOutcome = 'success' | 'failure';

// The success and failure(exit) outcome boxes are structurally identical; only
// the accent color, label, and testid vary by outcome. Keep the per-outcome
// bits in one map so the box markup lives in a single place.
const OUTCOME_CONFIG: Record<
  AuthOutcome,
  { labelKey: string; accent: string; dot: string; testId: string }
> = {
  success: {
    labelKey: 'dialog.auth.success',
    accent: 'border-l-successful',
    dot: 'bg-successful',
    testId: 'auth-success-box',
  },
  failure: {
    labelKey: 'dialog.auth.failure',
    accent: 'border-l-destructive',
    dot: 'bg-destructive',
    testId: 'auth-failure-box',
  },
};

type AuthOutcomeBoxProps = {
  outcome: AuthOutcome;
  /** Whether the owning step is enabled; disabled mirrors the step card's
   *  dashed-border / empty-background treatment. */
  enabled: boolean;
  selected: boolean;
  hasError?: boolean;
  onClick: (e: React.MouseEvent) => void;
};

const AuthOutcomeBox = memo(
  ({ outcome, enabled, selected, hasError, onClick }: AuthOutcomeBoxProps) => {
    const { t } = useTranslation();
    const { labelKey, accent, dot, testId } = OUTCOME_CONFIG[outcome];

    return (
      <div
        className={cn(
          'w-full cursor-pointer rounded border bg-background p-1 transition-all',
          enabled ? 'border-stroke' : 'border-dashed border-foreground-muted',
          !hasError && enabled && 'hover:border-foreground-muted',
          selected && !hasError && 'border-informational hover:border-informational',
          hasError && 'ring-2 ring-destructive'
        )}
        role="button"
        tabIndex={0}
        onClick={onClick}
        onKeyUp={(e) => {
          if (isKeyEnter(e)) {
            onClick(e as unknown as React.MouseEvent);
          }
        }}
        data-testid={testId}
      >
        <div
          className={cn(
            'flex items-center gap-2 rounded border-l-2 px-3 py-1.5',
            accent,
            enabled && 'bg-bg-muted-light'
          )}
        >
          <div className={cn('h-2 w-2 shrink-0 rounded-full', dot)} />
          <span className="text-left text-sm font-semibold text-foreground">{t(labelKey)}</span>
        </div>
      </div>
    );
  }
);

AuthOutcomeBox.displayName = 'AuthOutcomeBox';

export default AuthOutcomeBox;
